// Normalized application error for every non-OK API response. Nothing
// outside src/api/ imports the RFC 9457 wire shape; this class is the
// boundary type the rest of the app sees.
//
// `detail` is the server's human-readable message. `title` is only the
// status's reason phrase ("Bad Request"); it is kept to tell a problem body
// from an opaque one (a proxy's HTML page), never shown.

export interface FieldErrorSpec {
    readonly path: string;
    readonly message: string;
    readonly code: string;
}

/** transport's placeholder for a body without a `code`. */
export const UNKNOWN_CODE = "UNKNOWN";

export class ApiError extends Error {
    readonly status: number;
    readonly code: string;
    /** Reason phrase; present only when the body was problem json. */
    readonly title?: string;
    readonly detail?: string;
    readonly instance?: string;
    readonly requestId?: string;
    readonly errors?: ReadonlyArray<FieldErrorSpec>;
    readonly params?: Readonly<Record<string, string | null>>;

    constructor(init: {
        status: number;
        code: string;
        title?: string;
        detail?: string;
        instance?: string;
        requestId?: string;
        errors?: ReadonlyArray<FieldErrorSpec>;
        params?: Record<string, string | null>;
    }) {
        super(`API error ${init.status}: ${init.code}`);
        this.name = "ApiError";
        this.status = init.status;
        this.code = init.code;
        this.title = init.title;
        this.detail = init.detail;
        this.instance = init.instance;
        this.requestId = init.requestId;
        this.errors = init.errors;
        this.params = init.params;
    }

    get isValidation(): boolean {
        return Array.isArray(this.errors) && this.errors.length > 0;
    }
}

/** 403: the session is valid but the account isn't authorized here. */
export function isForbidden(error: unknown): error is ApiError {
    return error instanceof ApiError && error.status === 403;
}
